import React, { useRef } from 'react';
import { Transition } from '@headlessui/react';
import { FormattedMessage } from 'react-intl';
import { ChevronDown } from '../components/svgs/ChevronDown';
import { useFocusTrap } from '../lib/hooks/useFocusTrap';
import { FCWithChildren } from '../lib/types';

interface SlideOutProps {
  id: string;
  isOpen: boolean;
  isContentReady?: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  className?: string;
  ariaLabel?: string;
}

const SlideOut: FCWithChildren<SlideOutProps> = ({
  id,
  isOpen,
  isContentReady = true,
  onClose,
  afterLeave,
  className,
  children,
  ariaLabel,
}) => {
  const transition = 'transition-all duration-300';
  const slideIn = 'translate-x-0 opacity-100';
  const slideOut = 'translate-x-full opacity-0';

  const slideOutRef = useRef<HTMLDivElement>(null);

  useFocusTrap({
    isContentReady,
    containerRef: slideOutRef,
    isActive: isOpen,
    options: {
      id,
      priority: 2,
      initialFocus: `#slide-out-back-button-${id}`,
      restoreFocus: true,
    },
  });

  const handleClose = () => {
    onClose();
  };

  return (
    <Transition
      as="div"
      role="dialog"
      id="slide-out-dialog"
      ref={slideOutRef}
      aria-label={ariaLabel}
      aria-modal="true"
      className={`p-4 lg:p-6 fixed bg-white w-full h-full top-0 left-0 transform z-50 overflow-y-scroll ${className}`}
      enter={transition}
      enterFrom={slideOut}
      enterTo={slideIn}
      leave={transition}
      leaveFrom={slideIn}
      leaveTo={slideOut}
      show={isOpen}
      afterLeave={afterLeave}
      appear
    >
      <>
        <div className="flex m-2 mb-7 text-theme-primary">
          <button
            className="flex cursor-pointer items-center print:hidden"
            data-testid="slide-out-back-button"
            onClick={handleClose}
            id={`slide-out-back-button-${id}`}
            type="button"
          >
            <ChevronDown
              aria-hidden="true"
              className="w-4 fill-current transform-gpu rotate-90"
            />
            <span className="text-sm pl-2 font-bold">
              <FormattedMessage
                defaultMessage="Back"
                description="SlideOut back button text"
              />
            </span>
          </button>
        </div>
        {children}
      </>
    </Transition>
  );
};

export default SlideOut;
